import { CONTRACT_VERSION, type PublicBrandDto, type Theme, type UiLang } from "@imagora/contracts";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Toaster } from "./components/Toast";
import { TooltipProvider } from "./components/Tooltip";
import { I18nProvider, loadLanguage } from "./i18n";
import type { Dictionary } from "./i18n/format";
import { api, bridge, pushEnv, queryClient, sessionStore } from "./lib/runtime";
import { BrandContext, SessionContext } from "./lib/session-context";
import { useSessionState } from "./lib/use-session-state";
import { useTheme } from "./lib/use-theme";
import { pushOpenAction, syncPushOnOpen } from "./lib/web-push";
import type { AppRouter } from "./routes/router";

export interface AppProps {
  router: AppRouter;
  initialLang: UiLang;
  initialDictionary: Dictionary;
  themeHint: Theme | undefined;
}

const FALLBACK_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
// s10-login-b: the code alone, until the API says which providers this screen offers.
const NO_PROVIDERS: PublicBrandDto["loginProviders"] = [];

function currentPath(router: AppRouter): string {
  return router.state.location.href;
}

export function App({ router, initialLang, initialDictionary, themeHint }: AppProps) {
  const state = useSessionState();
  const session = state.status === "ready" ? state.session : null;
  const [publicBrand, setPublicBrand] = useState<PublicBrandDto | null>(null);
  const [brandFailed, setBrandFailed] = useState(false);
  const { resolved, setLive } = useTheme(session, themeHint, publicBrand);
  const [{ lang, dictionary }, setLanguage] = useState({ lang: initialLang, dictionary: initialDictionary });
  const latestLangRequest = useRef(initialLang);
  const userChoseLanguage = useRef(false);

  const applyLang = useCallback((next: UiLang) => {
    latestLangRequest.current = next;
    // The old text stays until the new one is here; a slower, older request must not overwrite a newer choice.
    void loadLanguage(next).then((loaded) => {
      if (latestLangRequest.current === next) setLanguage({ lang: next, dictionary: loaded });
    });
  }, []);

  const setLang = useCallback(
    (next: UiLang) => {
      userChoseLanguage.current = true;
      applyLang(next);
    },
    [applyLang],
  );

  // A renewed session repeats the language from the token; it must not undo what the person picked meanwhile.
  const sessionLang = session?.lang;
  useEffect(() => {
    if (sessionLang && !userChoseLanguage.current) applyLang(sessionLang);
  }, [sessionLang, applyLang]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(
    () =>
      bridge.subscribe((message) => {
        if (message.type === "imagora:set-theme") setLive(message.theme);
        else if (message.type === "imagora:set-lang") setLang(message.lang);
        else if (message.type === "imagora:navigate") void router.navigate({ to: message.path });
        else if (message.type === "imagora:viewport") {
          const { style } = document.documentElement;
          style.setProperty("--vp-top", `${message.top}px`);
          style.setProperty("--vp-height", `${message.height}px`);
        }
      }),
    [router, setLang, setLive],
  );

  // After the session, because only then is the parent's origin known to be one that may hear from us.
  const announced = useRef(false);
  const hasSession = session !== null;
  useEffect(() => {
    if (!hasSession || announced.current) return;
    announced.current = true;
    let lastPath = currentPath(router);
    bridge.send({ type: "imagora:ready", contract: CONTRACT_VERSION, path: lastPath });
    return router.subscribe("onResolved", () => {
      const path = currentPath(router);
      if (path === lastPath) return;
      lastPath = path;
      bridge.send({ type: "imagora:navigate", path });
    });
  }, [hasSession, router]);

  // A tap on a notice while the app is open: the worker can't move a page it doesn't control, so it says where and this does.
  useEffect(() => {
    if (bridge.embedded || !("serviceWorker" in navigator)) return;
    const container = navigator.serviceWorker;
    const onMessage = (event: MessageEvent) => {
      const current = sessionStore.getState();
      const action = pushOpenAction(event.data, current.status === "ready" ? current.session.tenant.id : null);
      if (action?.kind === "go") router.history.push(action.path);
      else if (action?.kind === "reload") window.location.assign(action.href);
    };
    container.addEventListener("message", onMessage);
    container.startMessages();
    return () => container.removeEventListener("message", onMessage);
  }, [router]);

  const tenantId = session?.tenant.id;
  useEffect(() => {
    if (tenantId && sessionLang) void syncPushOnOpen(pushEnv, api.notifications, { tenantId, lang: sessionLang }).catch(() => undefined);
  }, [tenantId, sessionLang]);

  // Opened directly, the address says whose pages these are. Embedded, the host's token says, and nothing is shown before it.
  useEffect(() => {
    if (bridge.embedded) return;
    let current = true;
    void api.account
      .brand(window.location.hostname)
      .then((loaded) => {
        if (current) setPublicBrand(loaded);
      })
      .catch(() => {
        // The code page then asks without the bot check; where it is on, the API's refusal says so on the page.
        if (current) setBrandFailed(true);
      });
    return () => {
      current = false;
    };
  }, []);

  // Empty until known: no page may show a name other than its host's, not even for the moment before the host's arrives.
  const known = session !== null || publicBrand !== null;
  const productName = known ? resolved.productName : "";
  const botCheckSiteKey = publicBrand ? publicBrand.botCheckSiteKey : brandFailed ? null : undefined;
  const brand = useMemo(
    // s10-login-b: loginProviders
    () => ({ productName, logo: known ? resolved.logo : null, directAccounts: publicBrand?.directAccounts ?? false, botCheckSiteKey, loginProviders: publicBrand?.loginProviders ?? NO_PROVIDERS }),
    [productName, known, resolved.logo, publicBrand?.directAccounts, botCheckSiteKey, publicBrand?.loginProviders],
  );

  useEffect(() => {
    document.title = productName;
  }, [productName]);

  return (
    <I18nProvider lang={lang} dictionary={dictionary} timeZone={session?.tenant.timezone ?? FALLBACK_TIME_ZONE} setLang={setLang}>
      <QueryClientProvider client={queryClient}>
        <SessionContext.Provider value={session}>
          <BrandContext.Provider value={brand}>
            <TooltipProvider>
              <RouterProvider router={router} />
              <Toaster />
            </TooltipProvider>
          </BrandContext.Provider>
        </SessionContext.Provider>
      </QueryClientProvider>
    </I18nProvider>
  );
}
